import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import { ArrowRight, Menu, X, Phone, Mail, MapPin, Clock, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { SEO } from "@/components/SEO";

// Base path for subfolder deployment
const BASENAME = "/emergent-landing";

// WordPress API Configuration
const WORDPRESS_URL = "https://www.mostwanted.ro";
const WP_API = `${WORDPRESS_URL}/wp-json/wp/v2`;
const WP_PORTFOLIO_API = `${WORDPRESS_URL}/wp-json/mostwanted/v1/portfolio`;

// Contact Form 7 Configuration - Update with your form ID
const CF7_FORM_ID = "1234"; // Replace with your actual CF7 form ID
const CF7_API = `${WORDPRESS_URL}/wp-json/contact-form-7/v1/contact-forms/${CF7_FORM_ID}/feedback`;

// Fallback to local API if CF7 not configured
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOCAL_API = `${BACKEND_URL}/api`;

// ===========================================
// PORTFOLIO DATA - Update these when you add new projects in WordPress
// These are your actual portfolio items from mostwanted.ro/portfolio/
// ===========================================
const portfolioItems = [
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2026/03/Coperta_restaurant_delise_turda_cluj_most-wanted-750x499.jpg",
    title: "Restaurant Delise Turda",
    category: "Restaurant",
    link: "https://www.mostwanted.ro/portfolio/design-restaurant-turda-delise/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2025/07/da-coffee_interior-design-portfolio-cover_mostwanted_album-cover-750x499.jpg",
    title: "Da Coffee Targu Mures",
    category: "Cafenea",
    link: "https://www.mostwanted.ro/portfolio/design-interior-da-coffee-targu-mures/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2025/02/Cover-Image_camera-hotel-B-750x499.jpg",
    title: "Luxury Hotel Room Design",
    category: "Hotel",
    link: "https://www.mostwanted.ro/portfolio/luxury-hotel-room-design-modern-retreat/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2024/11/Apartament-G-mostwanted-showcase-02-cover-750x499.jpg",
    title: "Airbnb Apartment Design",
    category: "Apartament",
    link: "https://www.mostwanted.ro/portfolio/design-interior-apartment-airbnb/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2024/10/cover-photo-for-refo-cafe-image-galery-02-750x499.jpg",
    title: "Refo Cafe",
    category: "Cafenea",
    link: "https://www.mostwanted.ro/portfolio/refo-cafe/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2024/10/Coperta_randare_restaurant_modern_turda_cluj_most-wanted-750x499.jpg",
    title: "Restaurant Delise",
    category: "Restaurant",
    link: "https://www.mostwanted.ro/portfolio/design-interior-restaurant-delise/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2024/12/BAaa2_residential_design_apartment_Showcase-750x499.jpg",
    title: "Modern Airbnb Rental",
    category: "Apartament",
    link: "https://www.mostwanted.ro/portfolio/modern-airbnb-rental-interior-design-project/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2023/06/cover-image-chi-cafe-floresti-design-by-most-wanted-design.jpg",
    title: "Chi Cafe Floresti",
    category: "Cafenea",
    link: "https://www.mostwanted.ro/portfolio/design-cafenea-chi-cafe/"
  },
  {
    image: "https://www.mostwanted.ro/wp-content/uploads/2021/06/cover-image-clinica-privata-de-ginecologie-gunea-design-by-most-wanted-design_cover.jpg",
    title: "Clinica Gynia",
    category: "Comercial",
    link: "https://www.mostwanted.ro/portfolio/design-receptie-gynia/"
  }
];

// Static content
const services = [
  { title: "Concept de Design", desc: "Oferim solutii unice de design interior, adaptate fiecarui proiect, cu accent pe creativitate si functionalitate.", link: `${WORDPRESS_URL}/categories/hospitaly/` },
  { title: "Management de Proiect", desc: "Coordonam si gestionam implementarea pentru rezultate eficiente si de calitate.", link: `${WORDPRESS_URL}/service/managementul-de-proiect/` },
  { title: "Randari 3D", desc: "Vizualizeaza proiectul tau inainte de implementare, cu randari realiste si detaliate.", link: `${WORDPRESS_URL}/categories/randari-3d/` },
  { title: "Implementare", desc: "Asiguram executia impecabila a fiecarui proiect, transformand viziunea in realitate.", link: `${WORDPRESS_URL}/contact/` },
  { title: "Design HoReCa", desc: "Specializati in transformarea spatiilor pentru restaurante, cafenele, hoteluri si pensiuni.", link: `${WORDPRESS_URL}/categories/hospitaly/` },
  { title: "Design Birouri", desc: "Cream spatii de lucru moderne, functionale si inspirationale pentru echipa ta.", link: `${WORDPRESS_URL}/categories/office/` }
];

const testimonials = [
  { name: "Vlad Florea", company: "Trend Furniture", text: "Colaborarea cu Most Wanted Design pe proiectele clientilor nostri a fost una excelenta. Studioul demonstreaza o viziune holistica in design, dezvoltand solutii perfect adaptate nevoilor specifice ale fiecarui client." },
  { name: "Daniel Cosarca", company: "Client", text: "Very good services. With a very well-developed aesthetic eye, and working with teams of professionals, it was a very pleasant experience." },
  { name: "Sebastian Schapitz", company: "Client", text: "Very nice contact combined with best work. Always happy!" },
  { name: "Boros Melinda", company: "Client", text: "I worked with them on several projects. Every time they managed to find the best solution. Helped with the orders and supervised the execution." }
];

// Helper functions
const getFeaturedImage = (post) => {
  if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
    return post._embedded['wp:featuredmedia'][0].source_url;
  }
  const imgMatch = post.content?.rendered?.match(/<img[^>]+src="([^">]+)"/);
  return imgMatch ? imgMatch[1] : null;
};

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={staggerContainer} className={className}>
      {children}
    </motion.div>
  );
};

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Proiecte", href: "#proiecte" },
    { name: "Servicii", href: "#servicii" },
    { name: "Despre Noi", href: "#despre" },
    { name: "Blog", href: "#blog" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <>
      <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm" : "bg-white"}`} data-testid="navigation">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20 md:h-24">
            <a href="#hero" className="font-['Cormorant_Garamond'] text-xl md:text-2xl tracking-wide text-[#1a1a1a] z-50" data-testid="nav-logo">
              MOST <span className="font-normal">WANTED</span> DESIGN
            </a>
            <button onClick={() => setIsOpen(!isOpen)} className="z-50 w-8 h-6 flex flex-col justify-between" data-testid="menu-toggle">
              <span className={`w-full h-[2px] bg-[#1a1a1a] block transition-transform ${isOpen ? 'rotate-45 translate-y-[10px]' : ''}`} />
              <span className={`w-full h-[2px] bg-[#1a1a1a] block transition-opacity ${isOpen ? 'opacity-0' : ''}`} />
              <span className={`w-full h-[2px] bg-[#1a1a1a] block transition-transform ${isOpen ? '-rotate-45 -translate-y-[10px]' : ''}`} />
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-white z-40 flex items-center justify-center">
            <div className="flex flex-col items-center gap-8">
              {navLinks.map((link, i) => (
                <motion.a key={link.name} href={link.href} onClick={() => setIsOpen(false)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }} className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#1a1a1a] hover:text-[#737373] transition-colors">
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Slider - Fetches first 3 portfolio items from WordPress API
const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const response = await axios.get(WP_PORTFOLIO_API);
        const heroData = response.data.slice(0, 3).map(item => ({
          image: item.image,
          title: item.title,
          category: item.category || 'Proiect',
          link: item.link
        }));
        setSlides(heroData.length > 0 ? heroData : portfolioItems.slice(0, 3));
      } catch (error) {
        console.log("Using fallback hero slides");
        setSlides(portfolioItems.slice(0, 3));
      } finally {
        setLoading(false);
      }
    };
    fetchHeroSlides();
  }, []);

  const nextSlide = useCallback(() => {
    if (slides.length > 0) setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);
  
  const prevSlide = useCallback(() => {
    if (slides.length > 0) setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!isAutoPlaying || slides.length === 0) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide, slides.length]);

  if (loading) {
    return (
      <section id="hero" className="relative h-[70vh] md:h-[85vh] bg-[#f5f5f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#737373]" />
      </section>
    );
  }

  return (
    <section id="hero" className="relative h-[70vh] md:h-[85vh] overflow-hidden" data-testid="hero-section">
      <div className="absolute -left-10 top-0 w-[120px] h-[600px] bg-[#1a1a1a] transform -rotate-[25deg] z-10 hidden md:block" />
      
      <AnimatePresence mode="wait">
        {slides[currentSlide] && (
          <motion.div key={currentSlide} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }} className="absolute inset-0">
            <img src={slides[currentSlide].image} alt={slides[currentSlide].title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30" />
          </motion.div>
        )}
      </AnimatePresence>

      {slides[currentSlide] && (
        <a href={slides[currentSlide].link} target="_blank" rel="noopener noreferrer" className="absolute bottom-8 right-8 md:bottom-12 md:right-12 z-20">
          <motion.div key={currentSlide} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-white px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#f5f5f5] transition-colors">
            <div>
              <p className="font-['Manrope'] text-xs text-[#737373] uppercase tracking-wider">{slides[currentSlide].category}</p>
              <p className="font-['Manrope'] text-sm md:text-base text-[#1a1a1a]">{slides[currentSlide].title}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#1a1a1a]" />
          </motion.div>
        </a>
      )}

      <button onClick={() => { prevSlide(); setIsAutoPlaying(false); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center" data-testid="hero-prev">
        <ChevronLeft className="w-5 h-5 text-[#1a1a1a]" />
      </button>
      <button onClick={() => { nextSlide(); setIsAutoPlaying(false); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 hover:bg-white flex items-center justify-center" data-testid="hero-next">
        <ChevronRight className="w-5 h-5 text-[#1a1a1a]" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, i) => (
          <button key={i} onClick={() => { setCurrentSlide(i); setIsAutoPlaying(false); }} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-white' : 'bg-white/40'}`} />
        ))}
      </div>
    </section>
  );
};

// Intro Section
const Intro = () => (
  <section className="py-16 md:py-24 bg-white">
    <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
      <AnimatedSection>
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] leading-tight mb-6">
            Design interior personalizat pentru afacerea ta
          </motion.h1>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-lg text-[#737373] mb-8">
            Solutii profesionale pentru HoReCa, proprietati de inchiriat si birouri.
          </motion.p>
          <motion.a variants={fadeInUp} href="#contact" className="inline-flex items-center gap-3 px-8 py-4 bg-[#1a1a1a] text-white font-['Manrope'] text-sm uppercase tracking-widest hover:bg-[#333] transition-colors" data-testid="intro-cta">
            Solicita o consultatie <ArrowRight className="w-4 h-4" />
          </motion.a>
        </div>
      </AnimatedSection>
    </div>
  </section>
);

// About Section
const About = () => (
  <section id="despre" className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="about-section">
    <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <AnimatedSection>
          <motion.div variants={fadeInUp} className="relative">
            <div className="absolute -left-6 top-1/4 w-[80px] h-[400px] bg-[#1a1a1a] transform -rotate-[25deg] hidden md:block" />
            <img src="https://www.mostwanted.ro/wp-content/uploads/2024/10/a-medium-shot-of-a-bearded-man-with-glasses-seated-contact-design-interior-most-wanted-cluj.png" alt="Most Wanted Design Team" className="w-full aspect-[4/5] object-cover" />
          </motion.div>
        </AnimatedSection>

        <AnimatedSection className="md:pl-8">
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4">Cine suntem noi?</motion.p>
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] leading-tight mb-8">Most Wanted Design</motion.h2>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed mb-6">
            <strong>Most Wanted Design</strong> – Este un brand de design interior Cluj, fondat de <strong>Gabriela Konhausner</strong> si <strong>Fulop Jozsef</strong>. Suntem specializati in transformarea spatiilor comerciale in medii vibrante si functionale, care imbina stilul cu personalitatea.
          </motion.p>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed mb-8">
            Ne adresam cu precadere sectorului HoReCa, proprietatilor de inchiriat, spatiilor comerciale si birourilor. Prin angajamentul nostru fata de creativitate, onestitate si profesionalism, realizam design de durata.
          </motion.p>
          <motion.div variants={fadeInUp}>
  <Link to="/about" className="inline-flex items-center gap-3 px-8 py-4 border border-[#1a1a1a] font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white transition-colors">
    Afla mai multe <ArrowRight className="w-4 h-4" />
  </Link>
</motion.div>
        </AnimatedSection>
      </div>
    </div>
  </section>
);

// Projects Section - Fetches 6 latest portfolio items from WordPress API
const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(WP_PORTFOLIO_API);
        const portfolioData = response.data.slice(0, 6).map((item, index) => ({
          image: item.image,
          title: item.title,
          category: item.category || 'Proiect',
          link: item.link,
          aspect: index === 0 || index === 3 ? 'tall' : 'square'
        }));
        setProjects(portfolioData);
      } catch (error) {
        console.log("Using fallback portfolio");
        setProjects(portfolioItems.slice(0, 6));
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <section id="proiecte" className="py-16 md:py-24 bg-white" data-testid="projects-section">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4">Portofoliu</motion.p>
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] mb-12">Proiectele noastre</motion.h2>
        </AnimatedSection>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#737373]" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {projects.map((project, i) => (
              <motion.a
                key={i}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={`group relative overflow-hidden cursor-pointer bg-[#f5f5f5] ${project.aspect === 'tall' ? 'row-span-2' : ''}`}
                data-testid={`project-${i}`}
              >
                {project.image ? (
                  <img src={project.image} alt={project.title} className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${project.aspect === 'tall' ? 'h-full min-h-[300px]' : 'aspect-square'}`} />
                ) : (
                  <div className={`w-full bg-[#e5e5e5] flex items-center justify-center ${project.aspect === 'tall' ? 'h-full min-h-[300px]' : 'aspect-square'}`}>
                    <span className="font-['Cormorant_Garamond'] text-2xl text-[#737373]">MW</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="font-['Manrope'] text-xs text-white/70 uppercase tracking-wider mb-1">{project.category}</p>
                  <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-white">{project.title}</p>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/portfolio" className="inline-flex items-center gap-3 px-8 py-4 border border-[#1a1a1a] font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white transition-colors" data-testid="projects-more">
            Vezi toate proiectele <ArrowRight className="w-4 h-4" />
          </Link> 
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => (
  <section id="servicii" className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="services-section">
    <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
      <AnimatedSection>
        <div className="grid md:grid-cols-2 gap-12 mb-12">
          <div>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4">Servicii</motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a]">Asa lucram</motion.h2>
          </div>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed self-end">
            Un proces structurat in designul interior faciliteaza o comunicare eficienta intre parti. Avand o directie clara si etape bine definite, riscurile de a face greseli sunt reduse.
          </motion.p>
        </div>
      </AnimatedSection>

      <div className="border-t border-[#e5e5e5]">
        {services.map((service, i) => (
          <motion.a key={i} href={service.link} target="_blank" rel="noopener noreferrer" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="block py-6 md:py-8 border-b border-[#e5e5e5] cursor-pointer hover:bg-white transition-colors group" data-testid={`service-${i}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-baseline gap-6">
                <span className="font-['Manrope'] text-sm text-[#737373]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#1a1a1a] group-hover:translate-x-2 transition-transform">{service.title}</h3>
              </div>
              <p className="font-['Manrope'] text-[#737373] md:text-right md:max-w-md">{service.desc}</p>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

// Process Section
const Process = () => {
  const steps = [
    { title: "Consultare", desc: "Colectam informatii esentiale despre nevoile si preferintele clientului. Discutam detalii precum stilul dorit, functionalitatea spatiului, bugetul si termenele limita." },
    { title: "Conceptul de design", desc: "Elaboram un concept personalizat de design interior. Realizam schite, planuri si propuneri vizuale care sa reflecte viziunea clientului." },
    { title: "Implementare", desc: "Ne asiguram ca fiecare detaliu este executat conform planului. Monitorizam progresul si colaboram cu furnizorii pentru a transforma viziunea in realitate." }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4 text-center">Procesul nostru</motion.p>
          <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] mb-16 text-center">De la idee la realitate</motion.h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#1a1a1a] flex items-center justify-center">
                <span className="font-['Cormorant_Garamond'] text-2xl text-[#1a1a1a]">{i + 1}</span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#1a1a1a] mb-4">{step.title}</h3>
              <p className="font-['Manrope'] text-[#737373] leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => (
  <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="testimonials-section">
    <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
      <AnimatedSection>
        <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-white/50 mb-4">Testimoniale</motion.p>
        <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-white mb-12">Ce spun clientii</motion.h2>
      </AnimatedSection>

      <div className="grid md:grid-cols-2 gap-8">
        {testimonials.map((testimonial, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="p-8 border border-white/10" data-testid={`testimonial-${i}`}>
            <p className="font-['Cormorant_Garamond'] text-4xl text-white/20 mb-4">"</p>
            <p className="font-['Manrope'] text-white/70 leading-relaxed mb-6">{testimonial.text}</p>
            <div>
              <p className="font-['Cormorant_Garamond'] text-lg text-white">{testimonial.name}</p>
              <p className="font-['Manrope'] text-sm text-white/50">{testimonial.company}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Blog Section - Auto-updates from WordPress (ONLY BLOG POSTS)
const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${WP_API}/posts?per_page=3&_embed`);
        const blogPosts = response.data.map(post => ({
          id: post.id,
          title: stripHtml(post.title.rendered),
          excerpt: stripHtml(post.excerpt.rendered).substring(0, 120) + '...',
          link: post.link,
          date: post.date,
          image: getFeaturedImage(post)
        }));
        setPosts(blogPosts);
      } catch (error) {
        console.log("Could not fetch blog posts, using fallback");
        setPosts([
          { id: 1, title: "Luxul intunericului - cand spatiul vorbeste in soapta", excerpt: "Descopera estetica dark luxury: o abordare in care umbra, textura si linistea definesc...", link: `${WORDPRESS_URL}/blog-luxul-intunericului-design-interior-dark-luxury/`, date: "2025-10-21" },
          { id: 2, title: "Stabilirea unui buget pentru amenajare", excerpt: "Cum sa iti planifici bugetul pentru un proiect de design interior reusit...", link: `${WORDPRESS_URL}/journal/`, date: "2024-07-15" },
          { id: 3, title: "Tendinte in design interior 2025", excerpt: "Cele mai importante tendinte care vor defini designul interior in acest an...", link: `${WORDPRESS_URL}/journal/`, date: "2024-12-01" }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchPosts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="blog" className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="blog-section">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
        <AnimatedSection>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4">Blog</motion.p>
              <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a]">Ultimele articole</motion.h2>
            </div>
            <motion.a variants={fadeInUp} href={`${WORDPRESS_URL}/journal/`} target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] hover:text-[#737373] transition-colors">
              Vezi toate articolele →
            </motion.a>
          </div>
        </AnimatedSection>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#737373]" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <motion.a key={post.id} href={post.link} target="_blank" rel="noopener noreferrer" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white p-8 hover:-translate-y-1 transition-transform cursor-pointer group" data-testid={`blog-${i}`}>
                <p className="font-['Manrope'] text-sm text-[#737373] mb-4">
                  {new Date(post.date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <h3 className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#1a1a1a] mb-4">{post.title}</h3>
                <p className="font-['Manrope'] text-sm text-[#737373] mb-6">{post.excerpt}</p>
                <span className="font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] flex items-center gap-2 group-hover:gap-4 transition-all">
                  Citeste mai mult <ArrowRight className="w-4 h-4" />
                </span>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Try Contact Form 7 first
      const cf7Data = new FormData();
      cf7Data.append('your-name', formData.name);
      cf7Data.append('your-email', formData.email);
      cf7Data.append('your-phone', formData.phone || '');
      cf7Data.append('your-message', formData.message);

      try {
        const response = await axios.post(CF7_API, cf7Data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.status === 'mail_sent') {
          toast.success("Mesajul a fost trimis cu succes! Va vom contacta in curand.");
          setFormData({ name: "", email: "", phone: "", message: "" });
        } else {
          throw new Error('CF7 submission failed');
        }
      } catch (cf7Error) {
        // Fallback to local API
        await axios.post(`${LOCAL_API}/contact`, formData);
        toast.success("Mesajul a fost trimis cu succes! Va vom contacta in curand.");
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      toast.error("Eroare la trimiterea mesajului. Va rugam incercati din nou.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-white" data-testid="contact-section">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-2 gap-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.2em] text-[#737373] mb-4">Contact</motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] mb-8">Esti gata sa transformi spatiul tau?</motion.h2>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed mb-12">
              Hai sa transformam impreuna spatiul tau! Contacteaza-ne acum pentru o consultatie gratuita.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="flex items-center gap-4">
                <MapPin className="w-5 h-5 text-[#737373]" />
                <span className="font-['Manrope'] text-[#1a1a1a]">Bulevardul 21 Decembrie 1989, nr. 37, Cluj-Napoca 400124</span>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-[#737373]" />
                <a href="tel:+40740814449" className="font-['Manrope'] text-[#1a1a1a] hover:text-[#737373] transition-colors" data-testid="contact-phone">+40 740 814 449</a>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-[#737373]" />
                <a href="mailto:contact@mostwanted.ro" className="font-['Manrope'] text-[#1a1a1a] hover:text-[#737373] transition-colors" data-testid="contact-email">contact@mostwanted.ro</a>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="w-5 h-5 text-[#737373]" />
                <span className="font-['Manrope'] text-[#1a1a1a]">Luni - Vineri: 10:00 - 17:00</span>
              </div>
            </motion.div>
          </AnimatedSection>

          <AnimatedSection>
            <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="font-['Manrope'] text-sm uppercase tracking-widest text-[#737373] block mb-3">Nume *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-transparent border-b border-[#e5e5e5] py-3 text-[#1a1a1a] font-['Manrope'] focus:outline-none focus:border-[#1a1a1a] transition-colors" placeholder="Numele tau" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="font-['Manrope'] text-sm uppercase tracking-widest text-[#737373] block mb-3">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-transparent border-b border-[#e5e5e5] py-3 text-[#1a1a1a] font-['Manrope'] focus:outline-none focus:border-[#1a1a1a] transition-colors" placeholder="email@exemplu.com" data-testid="contact-email-input" />
              </div>
              <div>
                <label className="font-['Manrope'] text-sm uppercase tracking-widest text-[#737373] block mb-3">Telefon</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-transparent border-b border-[#e5e5e5] py-3 text-[#1a1a1a] font-['Manrope'] focus:outline-none focus:border-[#1a1a1a] transition-colors" placeholder="+40 7XX XXX XXX" data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="font-['Manrope'] text-sm uppercase tracking-widest text-[#737373] block mb-3">Mesaj *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full bg-transparent border-b border-[#e5e5e5] py-3 text-[#1a1a1a] font-['Manrope'] focus:outline-none focus:border-[#1a1a1a] transition-colors resize-none" placeholder="Spune-ne despre proiectul tau..." data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#1a1a1a] text-white font-['Manrope'] text-sm uppercase tracking-widest hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center justify-center gap-3" data-testid="contact-submit-btn">
                {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Se trimite...</> : <>Trimite mesajul <ArrowRight className="w-4 h-4" /></>}
              </button>
            </motion.form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 bg-[#1a1a1a]" data-testid="footer">
    <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
      <div className="flex flex-col md:flex-row justify-between items-center gap-8">
        <a href="#hero" className="font-['Cormorant_Garamond'] text-xl text-white">
          MOST <span className="font-normal">WANTED</span> DESIGN
        </a>
        
        <div className="flex gap-8">
          <a href="https://www.instagram.com/mostwanted.design/" target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm text-white/60 hover:text-white transition-colors">Instagram</a>
          <a href="https://www.facebook.com/mostwanted.ro" target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm text-white/60 hover:text-white transition-colors">Facebook</a>
          <a href={`${WORDPRESS_URL}/journal/`} target="_blank" rel="noopener noreferrer" className="font-['Manrope'] text-sm text-white/60 hover:text-white transition-colors">Blog</a>
        </div>

        <p className="font-['Manrope'] text-sm text-white/60">© 2025 Most Wanted Design. Toate drepturile rezervate.</p>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smooth: true });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="App bg-white min-h-screen">
      <SEO page="home" />
      <Toaster position="top-right" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', fontFamily: 'Manrope, sans-serif' } }} />
      <Navigation />
      <HeroSlider />
      <Intro />
      <About />
      <Projects />
      <Services />
      <Process />
      <Testimonials />
      <Blog />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
